import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import SEO from '../components/SEO';

const CATEGORIES = ['general', 'research', 'guidelines', 'reports', 'presentations', 'templates'];

const FILE_ICONS = {
  pdf: 'fa-file-pdf', doc: 'fa-file-word', docx: 'fa-file-word',
  xls: 'fa-file-excel', xlsx: 'fa-file-excel',
  ppt: 'fa-file-powerpoint', pptx: 'fa-file-powerpoint',
  jpg: 'fa-file-image', jpeg: 'fa-file-image', png: 'fa-file-image', gif: 'fa-file-image', webp: 'fa-file-image',
};

const CMS = () => {
  const { t } = useTranslation();
  const { token, isAuthenticated, isAdmin, user, API } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [publicDocs, setPublicDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const [uploadForm, setUploadForm] = useState({ title: '', description: '', category: 'general', members_only: true, file: null });

  useEffect(() => {
    if (isAuthenticated) fetchMemberDocs();
    fetchPublicDocs();
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) fetchMemberDocs();
    else fetchPublicDocs();
  }, [filterCategory]);

  const fetchPublicDocs = async () => {
    try {
      const params = filterCategory ? `?category=${filterCategory}` : '';
      const res = await axios.get(`${API}/documents/public${params}`);
      setPublicDocs(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fetchMemberDocs = async () => {
    try {
      const params = filterCategory ? `?category=${filterCategory}` : '';
      const res = await axios.get(`${API}/documents${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDocuments(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) return alert('Please select a file');
    setUploading(true);
    const fd = new FormData();
    fd.append('file', uploadForm.file);
    try {
      await axios.post(
        `${API}/documents/upload?title=${encodeURIComponent(uploadForm.title)}&description=${encodeURIComponent(uploadForm.description)}&category=${uploadForm.category}&members_only=${uploadForm.members_only}`,
        fd,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      setShowUpload(false);
      setUploadForm({ title: '', description: '', category: 'general', members_only: true, file: null });
      fetchMemberDocs();
    } catch (err) { alert(err.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
  };

  const handleDownload = async (doc, isPublic) => {
    try {
      const url = isPublic ? `${API}/documents/${doc.id}/public-download` : `${API}/documents/${doc.id}/download`;
      const headers = isPublic ? {} : { Authorization: `Bearer ${token}` };
      const res = await axios.get(url, { headers, responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) { alert('Download failed'); }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API}/documents/${docId}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchMemberDocs();
    } catch (err) { alert(err.response?.data?.detail || 'Delete failed'); }
  };

  const getFileIcon = (filename) => {
    const ext = filename?.split('.').pop().toLowerCase();
    return FILE_ICONS[ext] || 'fa-file';
  };

  const formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const activeDocs = isAuthenticated ? documents : publicDocs;

  return (
    <div data-testid="cms-page">
      <SEO path="/cms" title="CMS & Documents" description="Access AFHOS publications, guidelines, research documents, and resources on hypertension management in Africa." />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <p className="overline">{t('cms.overline')}</p>
          <h1 data-testid="cms-title">{t('cms.title')}</h1>
          <p style={{ color: 'var(--muted)' }}>{t('cms.subtitle')}</p>
        </div>
        {isAuthenticated && (
          <button className="btn" onClick={() => setShowUpload(!showUpload)} data-testid="upload-doc-btn">
            {showUpload ? t('common.cancel') : '+ ' + t('cms.upload')}
          </button>
        )}
      </div>

      {!isAuthenticated && (
        <div className="card" style={{ padding: '1.25rem', marginBottom: '1.5rem', background: 'var(--brand-light)', borderLeft: '4px solid var(--brand)' }}>
          <p style={{ margin: 0 }}>
            <i className="fas fa-info-circle" style={{ marginRight: '0.5rem', color: 'var(--brand)' }}></i>
            {t('cms.signInForMore')} <a href="/auth" style={{ fontWeight: '600' }}>{t('auth.signIn')}</a>
          </p>
        </div>
      )}

      {showUpload && isAuthenticated && (
        <form onSubmit={handleUpload} className="card" style={{ padding: '1.5rem', marginBottom: '2rem' }} data-testid="upload-form">
          <h3 style={{ marginBottom: '1rem', color: 'var(--brand)' }}>{t('cms.uploadTitle')}</h3>
          <div className="form-group">
            <label>{t('cms.docTitle')} *</label>
            <input value={uploadForm.title} onChange={e => setUploadForm(p => ({...p, title: e.target.value}))} required data-testid="doc-title-input" />
          </div>
          <div className="form-group">
            <label>{t('cms.docDescription')}</label>
            <input value={uploadForm.description} onChange={e => setUploadForm(p => ({...p, description: e.target.value}))} data-testid="doc-description-input" />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>{t('cms.category')}</label>
              <select value={uploadForm.category} onChange={e => setUploadForm(p => ({...p, category: e.target.value}))} data-testid="doc-category-select">
                {CATEGORIES.map(c => <option key={c} value={c}>{t(`cms.${c}`)}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>{t('cms.file')} *</label>
              <input type="file" onChange={e => setUploadForm(p => ({...p, file: e.target.files[0]}))} accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp" data-testid="doc-file-input" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="radio" name="visibility" checked={uploadForm.members_only} onChange={() => setUploadForm(p => ({...p, members_only: true}))} data-testid="doc-members-only" />
              {t('cms.membersOnly')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="radio" name="visibility" checked={!uploadForm.members_only} onChange={() => setUploadForm(p => ({...p, members_only: false}))} data-testid="doc-public" />
              {t('cms.public')}
            </label>
          </div>
          <button type="submit" className="btn" disabled={uploading} data-testid="doc-upload-submit">{uploading ? t('cms.uploading') : t('cms.uploadBtn')}</button>
        </form>
      )}

      <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button className={`btn btn-small ${!filterCategory ? '' : 'btn-secondary'}`} onClick={() => setFilterCategory('')} data-testid="filter-all">{t('cms.all')}</button>
        {CATEGORIES.map(c => (
          <button key={c} className={`btn btn-small ${filterCategory === c ? '' : 'btn-secondary'}`} onClick={() => setFilterCategory(c)} data-testid={`filter-${c}`}>{t(`cms.${c}`)}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>{t('common.loading')}</div>
      ) : activeDocs.length === 0 ? (
        <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
          <p style={{ color: 'var(--muted)' }}>{t('cms.noDocs')} {filterCategory ? t('cms.tryDifferent') : (isAuthenticated ? t('cms.beFirst') : '')}</p>
        </div>
      ) : (
        <div className="documents-list">
          {activeDocs.map(doc => (
            <div key={doc.id} className="card document-card" data-testid={`doc-card-${doc.id}`} style={{ padding: '1rem 1.5rem', marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <i className={`fas ${getFileIcon(doc.original_filename)} file-icon`} style={{ fontSize: '1.5rem', color: 'var(--brand)', width: '2rem', textAlign: 'center' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.2rem' }}>
                    <h4 style={{ margin: 0 }}>{doc.title}</h4>
                    <span className={`badge ${doc.members_only ? 'badge-member' : 'badge-public'}`} style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem' }}>
                      {doc.members_only ? t('cms.membersOnly') : t('cms.public')}
                    </span>
                  </div>
                  <p style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>
                    {doc.original_filename} &middot; {formatSize(doc.size)} &middot; {doc.uploader_name} &middot; {new Date(doc.created_at).toLocaleDateString()}
                    {doc.category && <span className="badge badge-type" style={{ marginLeft: '0.5rem' }}>{doc.category}</span>}
                  </p>
                  {doc.description && <p style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>{doc.description}</p>}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn btn-small" onClick={() => handleDownload(doc, !isAuthenticated)} data-testid={`download-${doc.id}`}>{t('common.download')}</button>
                  {isAuthenticated && (doc.uploaded_by === user?.id || isAdmin) && (
                    <button className="btn btn-small btn-danger" onClick={() => handleDelete(doc.id)} data-testid={`delete-doc-${doc.id}`}>{t('common.delete')}</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CMS;
